import { z } from "zod";
import type { InteriorSample } from "../core/interiorSamples";
import type { Locale } from "../i18n";
import { filterSamples, type SpaceFilter } from "../home/model";
import { appShellCopy } from "../appShellContent";

const spaceSchema = z.enum(["all", "commercial", "office", "condo", "townhouse"]);
const styleSchema = z.enum(["all", "modern-tropical", "warm-modern", "thai-contemporary", "compact-living", "commercial"]);
const originSchema = z.enum(["all", "open-license", "ai-concept"]);
const sortSchema = z.enum(["curated", "title"]);
const savedSchema = z.array(z.string().regex(/^[a-z0-9-]{3,80}$/)).max(100);
export const scrapbookKey = "decobaan.public-ideas.v1";

export function readDiscoveryQuery(params: URLSearchParams) {
  return {
    query: (params.get("q") ?? "").normalize("NFKC").trim().slice(0, 120),
    space: spaceSchema.catch("all").parse(params.get("space")),
    style: styleSchema.catch("all").parse(params.get("style")),
    origin: originSchema.catch("all").parse(params.get("origin")),
    sort: sortSchema.catch("curated").parse(params.get("sort")),
    saved: params.get("saved") === "1",
    collection:z.uuid().catch("").parse(params.get("collection")),
    sample: z.string().regex(/^[a-z0-9-]{3,80}$/).catch("").parse(params.get("sample"))
  };
}
export type DiscoveryQuery = ReturnType<typeof readDiscoveryQuery>;

export function discoveryHref(locale: Locale, changes: Partial<DiscoveryQuery> = {}): string {
  const value = { ...readDiscoveryQuery(new URLSearchParams()), ...changes };
  const params = new URLSearchParams({ lang: locale });
  if (value.query) params.set("q", value.query.slice(0, 120));
  if (value.space !== "all") params.set("space", value.space);
  if (value.style !== "all") params.set("style", value.style);
  if (value.origin !== "all") params.set("origin", value.origin);
  if (value.sort !== "curated") params.set("sort", value.sort);
  if (value.saved) params.set("saved", "1");
  if (value.collection) params.set("collection",value.collection);
  if (value.sample) params.set("sample", value.sample);
  return `/discover?${params.toString()}`;
}

export function discoverSamples(samples: readonly InteriorSample[], query: DiscoveryQuery, savedIds: readonly string[], locale: Locale): InteriorSample[] {
  const term = query.query.toLocaleLowerCase();
  const result = filterSamples(samples, query.space).filter(sample =>
    (query.style === "all" || sample.style === query.style) &&
    (query.origin === "all" || sample.origin === query.origin) &&
    (!query.saved || savedIds.includes(sample.id)) &&
    (!term || [...Object.values(sample.title), sample.author, sample.location, sample.style, sample.spaceType, appShellCopy[locale].styles[sample.style], appShellCopy[locale].locations[sample.location]].join(" ").normalize("NFKC").toLocaleLowerCase().includes(term))
  );
  if (query.sort === "title") {
    const language = locale === "ko" || locale === "th" ? locale : "en";
    result.sort((left, right) => left.title[language].localeCompare(right.title[language], language));
  }
  return result;
}

export function readScrapbook(raw: string | null): string[] {
  if (raw === null || raw.length > 10000) return [];
  try {
    const decoded: unknown = JSON.parse(raw);
    const parsed = savedSchema.safeParse(decoded);
    return parsed.success ? [...new Set(parsed.data)] : [];
  } catch { return []; }
}

export function toggleSaved(ids: readonly string[], id: string): string[] {
  const valid = savedSchema.element.parse(id);
  return ids.includes(valid) ? ids.filter(item => item !== valid) : [...ids, valid].slice(-100);
}

export const discoverySpaces: readonly SpaceFilter[] = ["all", "condo", "townhouse", "commercial", "office"];
export const discoveryStyles = styleSchema.options;
export const discoveryOrigins = originSchema.options;
